import React, { useState } from 'react';
import { Lead, Team } from '../types';
import { MOCK_LEADS, TEAMS } from '../data/mockData';
import { StepIndicator } from '../components/StepIndicator';
import { LeadPool } from '../components/LeadPool';
import { TeamDistribution } from '../components/TeamDistribution';
import { DistributionReview } from '../components/DistributionReview';
import { motion, AnimatePresence } from 'framer-motion';
export function LeadDistributor() {
  const [currentStep, setCurrentStep] = useState(1);
  const [leads, setLeads] = useState<Lead[]>(MOCK_LEADS);
  const [teams] = useState<Team[]>(TEAMS);
  // Step 1: Assign leads to a team
  const handleAssignToTeam = (leadIds: string[], teamId: string) => {
    setLeads(prevLeads => prevLeads.map(lead => {
      if (leadIds.includes(lead.id)) {
        return {
          ...lead,
          assignedTeamId: teamId,
          status: 'assigned_team',
          // Reset user assignment if re-assigning team
          assignedUserId: undefined
        };
      }
      return lead;
    }));
  };
  // Step 2: Auto-distribute leads within a team to users
  const handleAutoDistribute = (teamId: string) => {
    const team = teams.find(t => t.id === teamId);
    if (!team) return;
    const teamLeads = leads.filter(l => l.assignedTeamId === teamId);
    const users = team.users;
    if (users.length === 0) return;
    // Round-robin distribution
    const updates = new Map<string, string>(); // leadId -> userId
    teamLeads.forEach((lead, index) => {
      const user = users[index % users.length];
      updates.set(lead.id, user.id);
    });
    setLeads(prevLeads => prevLeads.map(lead => {
      if (updates.has(lead.id)) {
        return {
          ...lead,
          assignedUserId: updates.get(lead.id),
          status: 'assigned_user'
        };
      }
      return lead;
    }));
  };
  const handleReset = () => {
    // Reset to initial state with new random leads or just reset status
    setLeads(prev => prev.map(l => ({
      ...l,
      status: 'unassigned',
      assignedTeamId: undefined,
      assignedUserId: undefined
    })));
    setCurrentStep(1);
  };
  const steps = ['Lead Pool', 'Distribute', 'Review & Push'];
  return <div className="min-h-screen bg-slate-50 pb-20" data-id="element-190">
      {/* Top Navigation Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30" data-id="element-191">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-192">
          <div className="flex items-center gap-2" data-id="element-193">
            <div className="h-8 w-8 bg-[#0D9488] rounded-md flex items-center justify-center" data-id="element-194">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-195">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" data-id="element-196" />
              </svg>
            </div>
            <span className="font-bold text-xl text-slate-900 tracking-tight" data-id="element-197">
              LeadFlow
            </span>
          </div>
          <div className="text-sm text-slate-500" data-id="element-198">
            Salesforce <span className="mx-2" data-id="element-199">→</span> Bonzo Integration
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-200">
        <StepIndicator currentStep={currentStep} steps={steps} data-id="element-201" />

        <div className="mt-8" data-id="element-202">
          <AnimatePresence mode="wait" data-id="element-203">
            <motion.div key={currentStep} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-204">
              {currentStep === 1 && <LeadPool leads={leads} teams={teams} onAssignToTeam={handleAssignToTeam} onNext={() => setCurrentStep(2)} data-id="element-205" />}

              {currentStep === 2 && <TeamDistribution leads={leads} teams={teams} onAutoDistribute={handleAutoDistribute} onNext={() => setCurrentStep(3)} onBack={() => setCurrentStep(1)} data-id="element-206" />}

              {currentStep === 3 && <DistributionReview leads={leads} teams={teams} onBack={() => setCurrentStep(2)} onReset={handleReset} data-id="element-207" />}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>;
}